import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HttpStatusCode } from '../dtos/http-status-code';
import { AuthService } from '../services/auth-service/auth.service';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService) {}

  /**
   * Adds the SSO token to all Web API requests and verifies all Web API responses to make sure that the
   * token was not rejected.
   *
   * @param req the request being made by application
   * @param next the next request handler in the chain
   * @returns the HTTP event
   */
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // copy the original request into a local variable, you cannot modify the original request
    let requestToForward = req;
    // start as not requiring authentication, if a non authenticated endpoint returns a 401, we don't want to trigger the auth process
    let isAuthenticated = false;
    // all urls behind the api/ path are authenticated
    if (req.url.startsWith('api')) {
      // set that this request requires authentication
      isAuthenticated = true;
      // get the access token
      const token = this.authService.getAccessToken();
      // if there is a token, set it in the headers
      if (token) {
        requestToForward = req.clone({
          headers: req.headers.set('Authorization', `Bearer ${token}`),
        });
      } else {
        // there was no token, start the auth process and abort this network request
        this.authService.initAuth();
        return EMPTY;
      }
    }
    // pass the request to the next handler
    return next.handle(requestToForward).pipe(
      tap({
        error: (error: HttpErrorResponse) => {
          // if the request comes back with an unauthorized status code AND the resource required authentication
          // remove the invalid token and start the auth process
          if (error.status === HttpStatusCode.unauthorized && isAuthenticated) {
            localStorage.removeItem('access_token');
            this.authService.initAuth();
          }
        },
      })
    );
  }
}
